import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import supabase from "../config/supabaseClient";
import Header from "../components/Header";
import Footer from "../components/Footer";

const Products = () => {
    const [products, setProducts] = useState([]);
    const [selectedVariants, setSelectedVariants] = useState({});

    useEffect(() => {
        const fetchProducts = async () => {
            const { data, error } = await supabase.from("products").select("*");

            if (error) {
                console.error("Error fetching products:", error);
            } else {
                setProducts(data);
                const initialVariants = {};
                data.forEach((product) => {
                    initialVariants[product.id] =
                        product.variants?.[0]?.color || product.variants?.[0]?.model || "";
                });
                setSelectedVariants(initialVariants);
            }
        };

        fetchProducts();
    }, []);

    const handleVariantChange = (productId, value) => {
        setSelectedVariants((prev) => ({ ...prev, [productId]: value }));
    };

    return (
        <div className="min-h-screen flex flex-col bg-black text-rose-300">
            {/* Header Section */}
            <Header />

            {/* Hero Section */}
            <section className="text-center py-12 mt-24 px-4">
                <h2
                    className="text-4xl md:text-5xl font-extrabold tracking-wide mb-4"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                    Unique Collection of <span className="text-white">Products</span>
                </h2>
                <p className="text-lg max-w-2xl mx-auto text-rose-200">
                    Experience the elegance and charm of our carefully curated collection.
                </p>
            </section>

            {/* Product Section */}
            <div className="flex-grow max-w-7xl mx-auto px-4 py-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-10">
                    {products.map((product) => {
                        const selectedVariant = product.variants?.find((variant) =>
                            product.name === "Shirt"
                                ? variant.color === selectedVariants[product.id]
                                : variant.model === selectedVariants[product.id]
                        );

                        return (
                            <motion.div
                                key={product.id}
                                className="p-4 rounded-lg glass shadow-lg transition-transform duration-300 hover:scale-[1.02] hover:shadow-rose-400/50"
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                {/* Product Link */}
                                <Link to={`/product/${product.id}/${selectedVariants[product.id]}`}>
                                    {/* Product Image */}
                                    <motion.img
                                        src={selectedVariant?.image_url || product.image_url}
                                        alt={product.name}
                                        className="w-full h-52 object-cover rounded-lg mb-4 shadow-md"
                                        whileHover={{ scale: 1.02 }}
                                    />

                                    {/* Product Details */}
                                    <h3 className="text-2xl font-semibold text-rose-300 text-center mb-2">
                                        {product.name}
                                    </h3>
                                    <p className="text-center text-rose-200 mb-2 text-sm md:text-base">
                                        {selectedVariant?.description || product.description}
                                    </p>
                                    <p className="text-center text-lg font-bold text-rose-400">
                                        ${selectedVariant?.price || product.price}
                                    </p>
                                </Link>

                                {/* Variant Selection */}
                                {product.variants && product.variants.length > 0 && (
                                    <div className="mt-4">
                                        <label
                                            className="block text-center text-lg text-rose-300 mb-2"
                                            htmlFor={`variant-${product.id}`}
                                        >
                                            {product.name === "Shirt" ? "Choose Color:" : "Select Model:"}
                                        </label>
                                        <select
                                            id={`variant-${product.id}`}
                                            className="w-full p-3 bg-black border border-rose-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-400 text-rose-300"
                                            value={selectedVariants[product.id]}
                                            onChange={(e) => handleVariantChange(product.id, e.target.value)}
                                        >
                                            {product.variants.map((variant, index) => (
                                                <option
                                                    key={index}
                                                    value={
                                                        product.name === "Shirt" ? variant.color : variant.model
                                                    }
                                                >
                                                    {product.name === "Shirt" ? variant.color : variant.model}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Footer Section */}
            <Footer />
        </div>
    );
};

export default Products;
